import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PAGE_SEO, SERVICE_SEO } from "../../data/seo";
import { SERVICES } from "../../data/services";

const PAGES = [
  ...Object.keys(PAGE_SEO).map((p) => ({ path: p, label: p === "/" ? "Home" : p.slice(1).replace(/^\w/, (c) => c.toUpperCase()), defaults: PAGE_SEO[p] })),
  ...SERVICES.map((s) => ({ path: `/services/${s.slug}`, label: s.name, defaults: SERVICE_SEO[s.slug] })),
];

export default function SeoSettings() {
  const { isAdmin } = useAuth();
  const [s, setS] = useState(null);
  const [sel, setSel] = useState(PAGES[0].path);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;

  const page = PAGES.find((p) => p.path === sel);
  const ov = s.seo?.[sel] || {};
  const val = (k) => ov[k] ?? page.defaults[k] ?? "";
  const set = (k) => (e) => setS({ ...s, seo: { ...s.seo, [sel]: { ...ov, [k]: e.target.value } } });
  const reset = () => { const seo = { ...s.seo }; delete seo[sel]; setS({ ...s, seo }); };
  const save = async () => {
    const { id, key, updated_at, ...body } = s;
    try { const { data } = await api.put("/admin/settings", body); setS(data); toast.success("SEO settings saved"); } catch (e) { toast.error(errorMessage(e)); }
  };
  const len = (k, max) => { const n = val(k).length; return <span className={`font-mono text-[11px] ${n > max ? "text-rose-600" : "text-slate-400"}`}>{n}/{max}</span>; };

  return (
    <div data-testid="admin-seo" className="grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div className="eyebrow">Search optimisation</div>
        <h1 className="mt-1 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Page SEO</h1>
        <p className="mt-2 text-sm text-slate-600">Override the title, description and keywords shown to Google for any page. Leave blank to use the built-in defaults.</p>
        <ul className="mt-6 space-y-1">
          {PAGES.map((p) => (
            <li key={p.path}><button data-testid={`seo-page-${p.path.replace(/\//g, "-") || "home"}`} onClick={() => setSel(p.path)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${sel === p.path ? "bg-[var(--navy)] text-white" : "text-slate-700 hover:bg-[var(--surface)]"}`}><span className="truncate">{p.label}</span>{s.seo?.[p.path] && <span className="ml-2 rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-slate-900">edited</span>}</button></li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-8">
        <div className="card space-y-5">
          <div className="font-mono text-xs text-slate-400">{sel}</div>
          <div><div className="flex items-center justify-between"><label className="label">Title tag</label>{len("title", 60)}</div><input data-testid="seo-title" className="input" value={val("title")} onChange={set("title")} disabled={!isAdmin} /></div>
          <div><div className="flex items-center justify-between"><label className="label">Meta description</label>{len("description", 155)}</div><textarea data-testid="seo-description" className="input min-h-[96px]" value={val("description")} onChange={set("description")} disabled={!isAdmin} /></div>
          <div><label className="label">Target keywords (comma-separated)</label><textarea data-testid="seo-keywords" className="input min-h-[72px]" value={val("keywords")} onChange={set("keywords")} disabled={!isAdmin} /></div>
          <div className="rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
            <div className="label">Google preview</div>
            <div className="text-[13px] text-slate-500">marksmanhealthcare.com{sel === "/" ? "" : sel}</div>
            <div className="mt-0.5 text-lg leading-snug text-[#1a0dab]">{val("title")} | MarksMan Healthcare</div>
            <div className="mt-1 text-sm text-slate-600">{val("description")}</div>
          </div>
          {isAdmin && <div className="flex gap-2"><button data-testid="seo-save" onClick={save} className="btn-primary"><Save size={15} /> Save</button>{s.seo?.[sel] && <button data-testid="seo-reset" onClick={reset} className="btn-outline"><RotateCcw size={15} /> Reset to default</button>}</div>}
        </div>
      </div>
    </div>
  );
}
